import { vm } from "../socket/config.js";
import {
    sendMessageToServer,
    sendChangeToServer,
    updateVersion,
} from "../socket/events.js";
import { targetToName, nameToTarget } from "../utils/scratch-helpers.js";
import { getLocalFlag, setLocalFlag } from "../utils/scratch-helpers.js";
import { isLocalMode } from "./local-mode.js";
import { isTargetLocked } from "../sprites/drag.js";
import { withBlocklyEventsDisabled } from "../blocks/index.js";

let lastMouseClient = [0, 0];
document.addEventListener("mousemove", (e) => {
    lastMouseClient = [e.clientX, e.clientY];
});

const canvasToScratchCoords = (clientX, clientY) => {
    const canvas = vm.renderer?.canvas;
    if (!canvas) return [0, 0];

    const rect = canvas.getBoundingClientRect();
    const nativeSize = vm.renderer.getNativeSize();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    return [
        (nativeSize[0] / rect.width) * (x - rect.width / 2),
        (nativeSize[1] / rect.height) * (y - rect.height / 2),
    ];
};

let activeDragInterval = null;
let activeDragTargetName = null;

const PAUSE_EVENT_HANDLING_TIMEOUT_MS = 10000;

let pauseEventHandling = false;
let pauseEventHandlingWatchdog = null;
let loadingProject = false;
let m_pauseWorkspaceUpdate = false;
let m_updateWorkspaceRequested = false;

export const setPauseEventHandling = (val) => {
    pauseEventHandling = val;

    if (pauseEventHandlingWatchdog) {
        clearTimeout(pauseEventHandlingWatchdog);
        pauseEventHandlingWatchdog = null;
    }

    if (val) {
        pauseEventHandlingWatchdog = setTimeout(() => {
            console.warn("pauseEventHandling was stuck on true; resetting it");
            pauseEventHandling = false;
            pauseEventHandlingWatchdog = null;
        }, PAUSE_EVENT_HANDLING_TIMEOUT_MS);
    }
};
export const isPauseEventHandling = () => pauseEventHandling;
export const setLoadingProject = (val) => {
    loadingProject = val;
};
export const isLoadingProject = () => loadingProject;

let originalToJSON = null;
export const getOriginalProjectJSON = (optTargetId) =>
    originalToJSON?.(optTargetId);

const BLANK_COSTUME_ID = "cd21514d0531fdffb22204e0ec5ed84a";
const ensureBlankCostumeCached = () => {
    const storage = vm.runtime?.storage;
    if (!storage || storage.get(BLANK_COSTUME_ID)) return;

    storage.cache(
        storage.AssetType.ImageVector,
        storage.DataFormat.SVG,
        new TextEncoder().encode(
            '<svg xmlns="http://www.w3.org/2000/svg" width="2" height="2" viewBox="0 0 2 2"></svg>',
        ),
        BLANK_COSTUME_ID,
    );
};

export const setupVMOverrides = () => {
    ensureBlankCostumeCached();

    const oldToJSON = vm.toJSON.bind(vm);
    originalToJSON = oldToJSON;
    const oldAddSprite = vm.addSprite.bind(vm);
    const oldDeleteSprite = vm.deleteSprite.bind(vm);
    const oldDuplicateSprite = vm.duplicateSprite.bind(vm);
    const oldUpdateBitmap = vm.updateBitmap.bind(vm);
    const oldUpdateSvg = vm.updateSvg.bind(vm);
    const oldAddBackdrop = vm.addBackdrop.bind(vm);
    const oldAddSound = vm.addSound.bind(vm);
    const oldDeleteSound = vm.deleteSound.bind(vm);
    const oldReorderSound = vm.reorderSound.bind(vm);
    const oldRenameSound = vm.renameSound.bind(vm);
    const oldDuplicateSound = vm.duplicateSound.bind(vm);
    const oldUpdateSoundBuffer = vm.updateSoundBuffer.bind(vm);
    const oldAddCostume = vm.addCostume.bind(vm);
    const oldDeleteCostume = vm.deleteCostume.bind(vm);
    const oldReorderCostume = vm.reorderCostume.bind(vm);
    const oldRenameCostume = vm.renameCostume.bind(vm);
    const oldDuplicateCostume = vm.duplicateCostume.bind(vm);
    const oldRenameSprite = vm.renameSprite.bind(vm);
    const oldPostSpriteInfo = vm.postSpriteInfo.bind(vm);
    const oldStartDrag = vm.startDrag.bind(vm);
    const oldStopDrag = vm.stopDrag.bind(vm);
    const oldLoadExtensionURL = vm.extensionManager.loadExtensionURL.bind(
        vm.extensionManager,
    );
    const oldRequestUpdateMonitor = vm.runtime.requestUpdateMonitor.bind(
        vm.runtime,
    );
    const oldRequestAddMonitor = vm.runtime.requestAddMonitor.bind(vm.runtime);

    // Override toJSON to include asset data
    const pickRuntimeAsset = (list, json, idx) =>
        (json?.assetId && list.find((a) => a?.assetId === json.assetId)) ||
        list[idx];

    const embedAssetData = (targetJson, runtimeTarget) => {
        if (!runtimeTarget?.sprite?.costumes) return;

        targetJson.costumes.forEach((costumeJson, costumeIdx) => {
            const runtimeCostume = pickRuntimeAsset(
                runtimeTarget.sprite.costumes,
                costumeJson,
                costumeIdx,
            );
            if (runtimeCostume?.asset?.data) {
                costumeJson.asset = {
                    assetType: runtimeCostume.asset.assetType,
                    dataFormat: runtimeCostume.asset.dataFormat,
                    data: Array.from(
                        runtimeCostume.asset.data instanceof Uint8Array
                            ? runtimeCostume.asset.data
                            : Uint8Array.from(
                                  Object.values(runtimeCostume.asset.data),
                              ),
                    ),
                };
            }
        });

        if (runtimeTarget?.sprite?.sounds) {
            targetJson.sounds.forEach((soundJson, soundIdx) => {
                const runtimeSound = pickRuntimeAsset(
                    runtimeTarget.sprite.sounds,
                    soundJson,
                    soundIdx,
                );
                if (runtimeSound?.asset?.data) {
                    soundJson.asset = {
                        assetType: runtimeSound.asset.assetType,
                        dataFormat: runtimeSound.asset.dataFormat,
                        data: Array.from(
                            runtimeSound.asset.data instanceof Uint8Array
                                ? runtimeSound.asset.data
                                : Uint8Array.from(
                                      Object.values(runtimeSound.asset.data),
                                  ),
                        ),
                    };
                }
            });
        }
    };

    vm.toJSON = function (optTargetId) {
        const json = oldToJSON(optTargetId);
        const parseJson = JSON.parse(json);

        if (optTargetId) {
            embedAssetData(parseJson, vm.runtime.getTargetById(optTargetId));
            return JSON.stringify(parseJson);
        }

        const originals = vm.runtime.targets.filter((t) => t.isOriginal);

        parseJson.targets.forEach((targetJson) => {
            const runtimeTarget = targetJson.isStage
                ? vm.runtime.getTargetForStage()
                : originals.find(
                      (t) => !t.isStage && t.sprite?.name === targetJson.name,
                  );

            embedAssetData(targetJson, runtimeTarget);
        });

        return JSON.stringify(parseJson);
    };

    // Override addSprite
    vm.addSprite = async function (spriteJSON) {
        ensureBlankCostumeCached();

        const result = await oldAddSprite(spriteJSON);
        if (!!pauseEventHandling) return result;

        const serializedEvent = {
            meta: "vm.spriteAdded",
            type: "sprite_addition",
            spriteData: spriteJSON,
            info: JSON.stringify(vm.editingTarget),
            spriteId: result,
        };

        sendMessageToServer("spriteAdded", serializedEvent);
        return result;
    };

    vm.duplicateSprite = async function (targetId) {
        const originalEditingTarget = vm.editingTarget;
        await oldDuplicateSprite(targetId);
        const newTarget = vm.editingTarget;

        if (
            !getLocalFlag() &&
            newTarget &&
            newTarget !== originalEditingTarget
        ) {
            const spriteData = await vm.exportSprite(
                newTarget.id,
                "uint8array",
            );

            sendMessageToServer("spriteAdded", {
                meta: "vm.spriteAdded",
                type: "sprite_addition",
                spriteData,
                info: JSON.stringify(newTarget),
                spriteId: newTarget.id,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
    };

    // Override addCostume
    vm.addCostume = async function (...args) {
        await oldAddCostume(...args);

        let ret = [args[0], args[1]];
        const target = !!args[2]
            ? targetToName(vm.runtime.getTargetById(args[2]))
            : targetToName(vm.editingTarget);

        if (ret[1]?.asset?.data) {
            const originalAsset = ret[1].asset;
            const dataBuffer =
                originalAsset.data instanceof Uint8Array
                    ? originalAsset.data
                    : Uint8Array.from(Object.values(originalAsset.data));

            ret[1].asset = await vm.runtime.storage.createAsset(
                ret[1].asset.assetType,
                ret[1].asset.dataFormat,
                dataBuffer,
                originalAsset.assetId,
                true,
            );

            const assetDataArray = Array.from(ret[1].asset.data);

            ret[1] = {
                name: ret[1].name || null,
                dataFormat: originalAsset.dataFormat,
                asset: ret[1].asset,
                assetData: assetDataArray,
                md5: `${ret[1].asset.assetId}.${originalAsset.dataFormat}`,
                assetId: ret[1].asset.assetId,
            };
        }

        if (!getLocalFlag()) {
            sendMessageToServer("costumeAdded", { target, ret: ret });
            updateVersion(vm);
        }
        setLocalFlag(false);
        return ret;
    };

    // Override deleteCostume
    vm.deleteCostume = (...args) => {
        oldDeleteCostume.bind(vm)(...args);

        const target = targetToName(vm.editingTarget);
        if (!getLocalFlag()) {
            sendMessageToServer("costumeDeleted", {
                target,
                costumeIndex: args[0],
            });
            updateVersion(vm);
        }
        setLocalFlag(false);
    };

    // Override reorderCostume
    vm.reorderCostume = function (targetId, costumeIndex, newIndex) {
        const reordered = oldReorderCostume(targetId, costumeIndex, newIndex);

        if (reordered && !getLocalFlag()) {
            sendMessageToServer("costumeReordered", {
                target: targetToName(vm.runtime.getTargetById(targetId)),
                costumeIndex,
                newIndex,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
        return reordered;
    };

    // Override renameCostume
    vm.renameCostume = function (costumeIndex, newName) {
        const oldName = vm.editingTarget?.sprite?.costumes?.[costumeIndex]?.name;

        oldRenameCostume(costumeIndex, newName);

        if (!getLocalFlag()) {
            const costume = vm.editingTarget?.sprite?.costumes?.[costumeIndex];

            if (costume && costume.name !== oldName) {
                sendMessageToServer("costumeRenamed", {
                    target: targetToName(vm.editingTarget),
                    costumeIndex,
                    name: costume.name,
                });
                updateVersion(vm);
            }
        }

        setLocalFlag(false);
    };

    vm.duplicateCostume = async function (costumeIndex) {
        const target = vm.editingTarget;
        const result = await oldDuplicateCostume(costumeIndex);

        if (!getLocalFlag()) {
            const copy = target?.sprite?.costumes?.[costumeIndex + 1];

            sendMessageToServer("costumeDuplicated", {
                target: targetToName(target),
                costumeIndex,
                name: copy?.name,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
        return result;
    };

    // Override renameSprite
    vm.renameSprite = function (targetId, newName) {
        const target = vm.runtime.getTargetById(targetId);
        const oldName = target?.sprite?.name;

        oldRenameSprite(targetId, newName);

        if (!getLocalFlag() && target && target.sprite.name !== oldName) {
            sendMessageToServer("spriteRenamed", {
                oldName,
                newName: target.sprite.name,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
    };

    // Override postSpriteInfo
    vm.postSpriteInfo = function (data) {
        const target = vm._dragTarget || vm.editingTarget;

        if (!getLocalFlag() && isTargetLocked(targetToName(target))) {
            return;
        }

        oldPostSpriteInfo(data);

        if (!getLocalFlag() && !isLocalMode()) {
            const payload = {};
            for (const field of [
                "x",
                "y",
                "direction",
                "size",
                "visible",
                "draggable",
                "rotationStyle",
            ]) {
                if (Object.prototype.hasOwnProperty.call(data, field)) {
                    payload[field] = target[field];
                }
            }

            if (activeDragTargetName === targetToName(target)) {
                delete payload.visible;
            }

            sendMessageToServer("spriteEditorStateChanged", {
                target: targetToName(target),
                ...payload,
            });
        }

        setLocalFlag(false);
    };

    // Override startDrag/stopDrag
    vm.startDrag = function (targetId) {
        oldStartDrag(targetId);

        const target = vm.runtime.getTargetById(targetId);
        if (!target) return;

        const targetName = targetToName(target);
        if (isLocalMode() || isTargetLocked(targetName)) return;

        if (activeDragInterval) {
            clearInterval(activeDragInterval);
            activeDragInterval = null;
        }

        const [scratchMouseX, scratchMouseY] = canvasToScratchCoords(
            ...lastMouseClient,
        );
        const dragOffset = [
            target.x - scratchMouseX,
            -(target.y + scratchMouseY),
        ];

        activeDragTargetName = targetName;
        sendMessageToServer("spriteDragStart", { target: targetName });

        activeDragInterval = setInterval(() => {
            const [sx, sy] = canvasToScratchCoords(...lastMouseClient);
            sendMessageToServer("spriteDragPosition", {
                target: targetName,
                x: sx + dragOffset[0],
                y: -(sy + dragOffset[1]),
            });
        }, 150);
    };

    vm.stopDrag = function (targetId) {
        try {
            oldStopDrag(targetId);
        } finally {
            if (activeDragInterval) {
                clearInterval(activeDragInterval);
                activeDragInterval = null;
                sendMessageToServer("spriteDragEnd", {
                    target: activeDragTargetName,
                });
            }
            activeDragTargetName = null;
        }
    };

    // Override updateBitmap
    vm.updateBitmap = async (...args) => {
        await oldUpdateBitmap(...args);

        const target = targetToName(vm.editingTarget);
        let costumeIndex = args[0];
        let bitmapResolution = args[4];
        let costume = vm.editingTarget.getCostumes()[costumeIndex];
        let sendCostume = JSON.parse(JSON.stringify(costume));
        delete sendCostume.asset;
        let asset = costume.asset;

        let bitmap = args[1];
        let w = bitmap.sourceWidth === 0 ? 0 : bitmap.width;
        let h = bitmap.sourceHeight === 0 ? 0 : bitmap.height;

        if (!getLocalFlag()) {
            sendMessageToServer("bitmapUpdate", {
                costume: sendCostume,
                target,
                costumeIndex,
                assetType: asset.assetType,
                assetData: asset.data ? Array.from(asset.data) : null,
                h,
                w,
                bitmapResolution,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
    };

    // Override updateSvg
    vm.updateSvg = async (...args) => {
        await oldUpdateSvg(...args);

        const target = targetToName(vm.editingTarget);
        const costumeIdx = args[0];
        const costume = vm.editingTarget.getCostumes()[costumeIdx];

        let sendCostume = JSON.parse(JSON.stringify(costume));
        delete sendCostume.asset;

        let asset = costume.asset;
        const assetData = asset.data ? Array.from(asset.data) : null;

        if (!getLocalFlag()) {
            sendMessageToServer("updateSvg", {
                costume: sendCostume,
                target,
                costumeIdx,
                assetType: asset.assetType,
                assetData,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
    };

    // Override addBackdrop
    vm.addBackdrop = async (...args) => {
        await oldAddBackdrop(...args);

        let ret = [args[0], args[1]];
        if (ret[1]?.asset?.data) {
            const originalAsset = ret[1].asset;
            const dataBuffer =
                originalAsset.data instanceof Uint8Array
                    ? originalAsset.data
                    : Uint8Array.from(Object.values(originalAsset.data));

            ret[1].asset = await vm.runtime.storage.createAsset(
                ret[1].asset.assetType,
                ret[1].asset.dataFormat,
                dataBuffer,
                originalAsset.assetId,
                true,
            );

            const assetDataArray = Array.from(ret[1].asset.data);

            ret[1] = {
                name: ret[1].name || null,
                dataFormat: originalAsset.dataFormat,
                asset: ret[1].asset,
                assetData: assetDataArray,
                md5: `${ret[1].asset.assetId}.${originalAsset.dataFormat}`,
                assetId: ret[1].asset.assetId,
                rotationCenterX: originalAsset.rotationCenterX,
                rotationCenterY: originalAsset.rotationCenterY,
            };
        }

        if (!getLocalFlag()) {
            sendMessageToServer("backdropAdded", { ret: ret });
            sendMessageToServer("updateVersion", { json: vm.toJSON() });
        }
        setLocalFlag(false);
        return ret;
    };

    // Override deleteSprite
    vm.deleteSprite = async function (id) {
        const targetToDelete = targetToName(vm.runtime.getTargetById(id));
        const result = await oldDeleteSprite(id);
        if (!!pauseEventHandling) return result;

        if (!getLocalFlag()) {
            sendMessageToServer("spriteDeleted", { target: targetToDelete });
            updateVersion(vm);
        } else setLocalFlag(false);

        return result;
    };

    // Override addSound
    vm.addSound = async function (sound) {
        await oldAddSound(sound);
        let soundData = null;

        if (sound?.asset?.data) {
            const originalAsset = sound.asset;
            const dataBuffer =
                originalAsset.data instanceof Uint8Array
                    ? originalAsset.data
                    : Uint8Array.from(Object.values(originalAsset.data));

            const reconstructedAsset = await vm.runtime.storage.createAsset(
                originalAsset.assetType,
                originalAsset.dataFormat,
                dataBuffer,
                originalAsset.assetId,
                true,
            );

            soundData = {
                name: sound.name || null,
                dataFormat: originalAsset.dataFormat,
                asset: {
                    assetType: reconstructedAsset.assetType,
                    dataFormat: reconstructedAsset.dataFormat,
                    data: Array.from(reconstructedAsset.data),
                    assetId: reconstructedAsset.assetId,
                },
                md5: `${reconstructedAsset.assetId}.${originalAsset.dataFormat}`,
                assetId: reconstructedAsset.assetId,
                rate: originalAsset.rate,
                sampleCount: originalAsset.sampleCount,
            };
        }

        if (!getLocalFlag()) {
            sendMessageToServer("soundAdded", {
                soundData: soundData,
                target: targetToName(vm.editingTarget),
                soundName: sound?.name || soundData?.name,
            });
            sendMessageToServer("updateVersion", { json: vm.toJSON() });
        }

        setLocalFlag(false);
    };

    // Override deleteSound
    vm.deleteSound = async function (idx) {
        await oldDeleteSound(idx);

        if (!getLocalFlag()) {
            sendMessageToServer("soundDeleted", {
                soundIdx: idx,
                target: targetToName(vm.editingTarget),
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
    };

    // Override reorderSound
    vm.reorderSound = function (targetId, soundIndex, newIndex) {
        const reordered = oldReorderSound(targetId, soundIndex, newIndex);

        if (reordered && !getLocalFlag()) {
            sendMessageToServer("soundReordered", {
                target: targetToName(vm.runtime.getTargetById(targetId)),
                soundIndex,
                newIndex,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
        return reordered;
    };

    // Override renameSound
    vm.renameSound = function (soundIndex, newName) {
        const oldName = vm.editingTarget?.sprite?.sounds?.[soundIndex]?.name;

        oldRenameSound(soundIndex, newName);

        if (!getLocalFlag()) {
            const sound = vm.editingTarget?.sprite?.sounds?.[soundIndex];

            if (sound && sound.name !== oldName) {
                sendMessageToServer("soundRenamed", {
                    target: targetToName(vm.editingTarget),
                    soundIndex,
                    name: sound.name,
                });
                updateVersion(vm);
            }
        }

        setLocalFlag(false);
    };

    // Override duplicateSound
    vm.duplicateSound = async function (soundIndex) {
        const target = vm.editingTarget;
        await oldDuplicateSound(soundIndex);

        if (!getLocalFlag()) {
            const copy = target?.sprite?.sounds?.[soundIndex + 1];

            sendMessageToServer("soundDuplicated", {
                target: targetToName(target),
                soundIndex,
                name: copy?.name,
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
    };

    // Override updateSoundBuffer
    vm.updateSoundBuffer = function (soundIndex, newBuffer, soundEncoding) {
        const target = vm.editingTarget;
        oldUpdateSoundBuffer(soundIndex, newBuffer, soundEncoding);

        const sound = target?.sprite?.sounds?.[soundIndex];

        if (soundEncoding && sound?.asset?.data && !getLocalFlag()) {
            sendMessageToServer("soundUpdated", {
                target: targetToName(target),
                soundIndex,
                sound: {
                    name: sound.name,
                    dataFormat: sound.dataFormat,
                    assetId: sound.assetId,
                    md5: sound.md5,
                    rate: sound.rate,
                    sampleCount: sound.sampleCount,
                },
                assetData: Array.from(sound.asset.data),
            });
            updateVersion(vm);
        }

        setLocalFlag(false);
    };

    // Override extensionManager.loadExtensionURL
    vm.extensionManager.loadExtensionURL = async function (extensionURL) {
        const result = await oldLoadExtensionURL(extensionURL);

        if (!getLocalFlag() && !isLoadingProject()) {
            sendMessageToServer("extensionAdded", { extensionURL });
            updateVersion(vm);
        }

        setLocalFlag(false);
        return result;
    };

    const STRUCTURAL_MONITOR_FIELDS = [
        "visible",
        "mode",
        "sliderMin",
        "sliderMax",
        "isDiscrete",
    ];

    const monitorToPlainObject = (monitor) =>
        typeof monitor.toObject === "function"
            ? monitor.toObject()
            : Object.fromEntries(monitor);

    vm.runtime.requestUpdateMonitor = function (monitor) {
        const result = oldRequestUpdateMonitor(monitor);

        if (
            result &&
            !getLocalFlag() &&
            STRUCTURAL_MONITOR_FIELDS.some((field) => monitor.has(field))
        ) {
            sendMessageToServer("monitorUpdated", {
                monitor: monitorToPlainObject(monitor),
            });
        }

        setLocalFlag(false);
        return result;
    };

    vm.runtime.requestAddMonitor = function (monitor) {
        const shouldBroadcast = !getLocalFlag() && !isLoadingProject();

        oldRequestAddMonitor(monitor);

        if (shouldBroadcast) {
            sendMessageToServer("monitorAdded", {
                monitor: monitorToPlainObject(monitor),
            });
        }

        setLocalFlag(false);
    };
};

export const setupWorkspaceOverrides = () => {
    let oldEWU = vm.emitWorkspaceUpdate.bind(vm);
    let oldTargUp = vm.emitTargetsUpdate.bind(vm);
    let oldSetEditingTarget = vm.setEditingTarget.bind(vm);

    vm.emitWorkspaceUpdate = function () {
        if (pauseEventHandling) return;
        if (m_pauseWorkspaceUpdate) {
            m_updateWorkspaceRequested = true;
            return;
        }

        withBlocklyEventsDisabled(oldEWU);
    };

    vm.emitTargetsUpdate = function (...args) {
        if (pauseEventHandling) {
            return;
        } else {
            oldTargUp(...args);
        }
    };

    vm.setEditingTarget = function (targetId) {
        const previousTarget = vm.editingTarget;
        const result = oldSetEditingTarget(targetId);

        if (!isLoadingProject() && vm.editingTarget !== previousTarget) {
            sendMessageToServer("spriteFocus", {
                target: targetToName(vm.editingTarget),
            });
        }

        return result;
    };
};

export const pauseWorkspaceUpdate = () => {
    m_pauseWorkspaceUpdate = true;
};
export const resumeWorkspaceUpdate = () => {
    m_pauseWorkspaceUpdate = false;
    if (m_updateWorkspaceRequested) vm.emitWorkspaceUpdate();
    m_updateWorkspaceRequested = false;
};
